import { effect, inject, Injectable } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import { TranslationsService } from './translations.service';

/**
 * Sincroniza los textos internos de PrimeNG (etiquetas de accesibilidad del paginador, de las
 * tablas y de los desplegables, y los mensajes de "sin resultados") con la sección `primeng`
 * de las traducciones.
 *
 * PrimeNG trae esos textos en inglés y solo se pueden cambiar con `PrimeNG.setTranslation`.
 * Una clave que todavía no exista en BD se omite para conservar el valor por defecto de
 * PrimeNG, así que activar este servicio nunca deja una etiqueta vacía.
 */
@Injectable({ providedIn: 'root' })
export class PrimeNgLocaleService {
  private readonly _primeng = inject(PrimeNG);
  private readonly _section = inject(TranslationsService).sectionSignal('primeng');

  /** Clave de traducción de cada etiqueta de accesibilidad de PrimeNG (`translation.aria`). */
  private static readonly ARIA_KEYS: Readonly<Record<string, string>> = {
    firstPageLabel: 'primeng_aria_first_page',
    prevPageLabel: 'primeng_aria_prev_page',
    previousPageLabel: 'primeng_aria_prev_page',
    nextPageLabel: 'primeng_aria_next_page',
    lastPageLabel: 'primeng_aria_last_page',
    pageLabel: 'primeng_aria_page',
    rowsPerPageLabel: 'primeng_aria_rows_per_page',
    jumpToPageDropdownLabel: 'primeng_aria_jump_to_page_dropdown',
    jumpToPageInputLabel: 'primeng_aria_jump_to_page_input',
    close: 'primeng_aria_close',
    selectAll: 'primeng_aria_select_all',
    unselectAll: 'primeng_aria_unselect_all',
    selectRow: 'primeng_aria_select_row',
    unselectRow: 'primeng_aria_unselect_row',
    expandRow: 'primeng_aria_expand_row',
    collapseRow: 'primeng_aria_collapse_row',
    showFilterMenu: 'primeng_aria_show_filter_menu',
    hideFilterMenu: 'primeng_aria_hide_filter_menu',
    trueLabel: 'primeng_aria_true',
    falseLabel: 'primeng_aria_false',
  };

  /** Claves de los mensajes de primer nivel de PrimeNG (`translation.*`). */
  private static readonly MESSAGE_KEYS: Readonly<Record<string, string>> = {
    emptyMessage: 'primeng_empty_message',
    emptyFilterMessage: 'primeng_empty_filter_message',
    chooseYear: 'primeng_choose_year',
    chooseMonth: 'primeng_choose_month',
    chooseDate: 'primeng_choose_date',
    prevDecade: 'primeng_prev_decade',
    nextDecade: 'primeng_next_decade',
    prevYear: 'primeng_prev_year',
    nextYear: 'primeng_next_year',
    prevMonth: 'primeng_prev_month',
    nextMonth: 'primeng_next_month',
  };

  constructor() {
    effect(() => {
      const section = this._section() as Record<string, unknown> | null;
      if (!section) {
        return;
      }
      const read = (key: string): string | undefined => {
        const value =
          (section[key] as string | undefined) ??
          (section['gene_code_tran_code_keys'] as Record<string, string> | undefined)?.[key];
        return typeof value === 'string' && value ? value : undefined;
      };

      const aria: Record<string, string> = {};
      for (const [field, key] of Object.entries(PrimeNgLocaleService.ARIA_KEYS)) {
        const value = read(key);
        if (value) {
          aria[field] = value;
        }
      }
      const messages: Record<string, string> = {};
      for (const [field, key] of Object.entries(PrimeNgLocaleService.MESSAGE_KEYS)) {
        const value = read(key);
        if (value) {
          messages[field] = value;
        }
      }

      // `setTranslation` fusiona solo el primer nivel: se parte de `aria` vigente para no perder
      // las etiquetas que esta sección no traduce.
      this._primeng.setTranslation({
        ...messages,
        aria: { ...this._primeng.translation.aria, ...aria },
      });
    });
  }
}
